import { RebuySDK } from '@rebuy/core-sdk';

class RebuyRecommendations extends HTMLElement {
  constructor() {
    super();
    this.sdk = null;
    this.swiper = null;
    this.recommendationType = this.getAttribute('data-recommendation-type') || 'top_sellers';
    this.limit = this.getAttribute('data-limit') || 8;
    this.productId = this.getAttribute('data-product-id') || null;
    this.staticId = this.getAttribute('data-static-id') || null;
    this.emptyMessage = this.getAttribute('data-empty-message') || 'No products to display.';
    this.desktopItems = this.getDesktopItems();
    this.swiperEl = this.querySelector('.rebuy-swiper');
    this.container = this.querySelector('.swiper-wrapper');
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  async waitForSwiper(maxAttempts = 50) {
    for (let i = 0; i < maxAttempts; i++) {
      if (typeof Swiper !== 'undefined') return true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return false;
  }

  async initialize() {
    if (!window.themeConfig?.rebuy?.key) {
      console.error('[RebuyRecommendations] Missing themeConfig.rebuy.key');
      return;
    }

    this.sdk = new RebuySDK({
      apiKey: window.themeConfig.rebuy.key,
    });

    if (this.sdk.session.initPromise) {
      await this.sdk.session.initPromise;
    }

    if (this.productId) {
      await this.sdk.tracking.productView(this.productId);
    }

    try {
      const products = await this.getProducts();
      if (products?.length) {
        await this.renderProducts(products);
      } else if (this.recommendationType === 'recently_viewed') {
        this.hideSection();
      } else {
        this.renderEmpty();
      }
    } catch (error) {
      console.error('[RebuyRecommendations] Failed to load products:', error);
      if (this.recommendationType === 'recently_viewed') {
        this.hideSection();
      } else {
        this.renderEmpty();
      }
    }
  }

  cleanup() {
    this.destroySwiper();
    this.sdk = null;
  }

  destroySwiper() {
    const instance = this.swiper || this.swiperEl?.swiper;
    if (instance) {
      instance.destroy(true, true);
    }
    this.swiper = null;
    if (this.swiperEl) {
      delete this.swiperEl.dataset.swiperInitialized;
    }
  }

  getProducts() {
    switch (this.recommendationType) {
      case 'static_recommendations':
        return this.sdk.products.fetchFromDataSource(this.staticId, { limit: this.limit, shopify_product_ids: [this.productId] });
      case 'top_sellers':
        return this.sdk.products.getTopSellers({ limit: this.limit });
      case 'similar_products':
        return this.sdk.products.getSimilar({ limit: this.limit, shopify_product_ids: [this.productId] });
      case 'recently_viewed':
        return this.sdk.products.getRecentlyViewed({ limit: this.limit });
      case 'previously_purchased':
        return this.sdk.products.getPurchased({ limit: this.limit });
      default:
        return this.sdk.products.getTopSellers({ limit: this.limit });
    }
  }

  parseSectionHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.querySelector('.shopify-section');
    return section ? section.innerHTML.trim() : html.trim();
  }

  getDesktopItems() {
    const value = Number(this.getAttribute('data-desktop-items'));
    if (!Number.isFinite(value)) return 4;
    return Math.min(Math.max(value, 1), 6);
  }

  getSwiperConfig() {
    if (this.hasAttribute('data-showcase-layout')) {
      return {
        speed: 300,
        spaceBetween: 20,
        slidesPerView: 1,
        loop: false,
        breakpoints: {
          390: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 2,
            spaceBetween: 0,
          },
        },
      };
    }

    return {
      speed: 300,
      spaceBetween: 0,
      slidesPerView: 1,
      loop: false,
      breakpoints: {
        390: {
          slidesPerView: 2,
          spaceBetween: 0,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 0,
        },
        1024: {
          slidesPerView: this.desktopItems,
          spaceBetween: 0,
        },
      },
    };
  }

  getCurrentSlidesPerView() {
    if (!this.swiper) return 1;
    const slidesPerView = Number(this.swiper.params.slidesPerView);
    return Number.isFinite(slidesPerView) ? slidesPerView : 1;
  }

  updateProgressBar() {
    if (!this.swiper || !this.swiperEl) return;

    const progressBar = this.swiperEl.querySelector('.rc-progress-bar');
    const progressBarLine = this.swiperEl.querySelector('.rc-progress-bar__line');
    if (!progressBar || !progressBarLine) return;

    const currentSlidesPerView = this.getCurrentSlidesPerView();
    const totalSlides = this.swiper.slides.length;
    const maxScrollableIndex = totalSlides - currentSlidesPerView;
    const rawProgress = maxScrollableIndex > 0 ? this.swiper.activeIndex / maxScrollableIndex : 1;
    const clampedProgress = Math.min(Math.max(rawProgress, 0), 1);
    const visualProgress = Math.max(clampedProgress * 100, 25);

    progressBarLine.style.width = `${visualProgress}%`;
    progressBar.style.opacity = totalSlides > currentSlidesPerView ? '1' : '0';
  }

  getNavButtons() {
    const showcaseRoot = this.closest('.product-showcase--upsell');
    if (showcaseRoot && this.hasAttribute('data-showcase-layout')) {
      return {
        prev: showcaseRoot.querySelector('.product-showcase__upsell-nav-btn--prev'),
        next: showcaseRoot.querySelector('.product-showcase__upsell-nav-btn--next'),
      };
    }

    return {
      prev: this.querySelector('.rebuy-recommendations__nav-btn--prev'),
      next: this.querySelector('.rebuy-recommendations__nav-btn--next'),
    };
  }

  initSwiper() {
    if (!this.swiperEl || !this.container) return;

    this.destroySwiper();

    const { prev: prevBtn, next: nextBtn } = this.getNavButtons();
    const config = this.getSwiperConfig();

    this.swiper = new Swiper(this.swiperEl, {
      ...config,
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      on: {
        init: () => this.updateProgressBar(),
        slideChange: () => this.updateProgressBar(),
        breakpoint: () => this.updateProgressBar(),
        resize: () => this.updateProgressBar(),
        update: () => this.updateProgressBar(),
      },
    });

    this.swiperEl.dataset.swiperInitialized = 'true';
    this.updateProgressBar();
  }

  async initSwiperWhenReady() {
    const swiperReady = await this.waitForSwiper();
    if (!swiperReady) {
      console.error('[RebuyRecommendations] Swiper is not available');
      return;
    }

    this.initSwiper();
  }

  async renderProducts(products) {
    if (!this.container) return;

    const cardPromises = products.map(async (product) => {
      if (!product.handle) return null;

      try {
        const response = await fetch(`/products/${product.handle}?section_id=rebuy-product-card`);
        if (!response.ok) return null;
        const html = await response.text();
        return this.parseSectionHtml(html);
      } catch {
        return null;
      }
    });

    const cards = await Promise.all(cardPromises);

    this.container.innerHTML = cards
      .filter(Boolean)
      .map((html) => `<div class="swiper-slide">${html}</div>`)
      .join('');

    document.dispatchEvent(new CustomEvent('theme:bind-atc', {
      detail: { root: this },
    }));

    await this.initSwiperWhenReady();
  }

  async renderEmpty() {
    if (!this.container) return;

    this.destroySwiper();
    this.container.innerHTML = `<div class="swiper-slide rebuy-recommendations__empty-slide"><p class="rebuy-recommendations__empty">${this.emptyMessage}</p></div>`;
  }

  hideSection() {
    this.destroySwiper();

    const sectionRoot =
      this.closest('.rebuy-recommendations-section') ||
      this.closest('.product-showcase--upsell') ||
      this.closest('[id^="shopify-section-"]');

    sectionRoot?.classList.add('hidden');
  }
}

if (!customElements.get('rebuy-recommendations')) {
  customElements.define('rebuy-recommendations', RebuyRecommendations);
}
